import objectAssignDeep from "./tools/object-assign-deep";

export default class MessageBox
{
    constructor(paramObj) {
        this.selector = paramObj.selector;
        this.baseClass = this.instance().className;
        this.hcs = paramObj.hcs;
    }

    /**
     * Open message dialog
     * @param {object} options - Options
     * @param {number} [buttons=MessageBox.MB_OK] - Which buttons shall be shown. For possible values see constants below.
     * Combine multiple values with binary or '|' like: MessageBox.MB_YES | MessageBox.MB_NO
     * @param {object} [buttonsTexts] - Override default buttons texts.
     * @param {object} [buttonClassCategories] - All buttons initially have the "hcs-primary" button class.
     * To change buttons looks you can override this class for every button and set another class name like "hcs-secondary".
     * @param {number} [messageType=MessageBox.MB_INFO] - Define type of message (info, error, ...). For possible values see constants below.
     * Set "null" to hide message type icon.
     * @param {string} [title] - Title text.
     * @param {string|DOMElement} [text] - Main text content, can also contain HTML code. Also a DOM element can be added.
     * @param {bool} [modal=true] - Show message box either modal or non-modal.
     * @param {bool} [closeOnEscape=true] - When enabled dialog can be closed using the escape key.
     * @param {string|Array} [classNames] - Add additional CSS class names or array containing list of class names.
     * @param {function} [onOpen] - Event being called when message box opens.
     * @param {function} [onOk] - Event being called when user clicks the OK button.
     * @param {function} [onCancel] - Event being called when user clicks the "OK" button.
     * @param {function} [onYes] - Event being called when user clicks the "Yes" button.
     * @param {function} [onNo] - Event being called when user clicks the "no" button.
     * @param {function} [onClose] - Event being called when user clicks the "Close" button.
     * @param {function} [onCopyClipboard] - Event being called when user clicks the "Copy to clipboard" button.
     */
    show(options) {
        var self = this;

        // Avoid to exceed HCS container size.
        this.instance().style.maxWidth = (this.hcs.ui.size().width * 0.85) + "px";
        this.instance().style.maxHeight = (this.hcs.ui.size().height * 0.85) + "px";

        this.instance(".hcs-buttons BUTTON", true).forEach((item) => {
            item.classList.remove("hcs-visible");
        });

        var opts = objectAssignDeep({}, {
            buttons: MessageBox.MB_OK,
            buttonTexts: {
                "ok": this.hcs.t.gui("Ok"),
                "cancel": this.hcs.t.gui("Cancel"),
                "yes": this.hcs.t.gui("Yes"),
                "no": this.hcs.t.gui("No"),
                "close": this.hcs.t.gui("Close"),
                "copy-clipboard": this.hcs.t.gui("CopyToClipboard")
            },
            buttonClassCategories: {
                "ok": "hcs-primary",
                "cancel": "hcs-primary",
                "yes": "hcs-primary",
                "no": "hcs-primary",
                "close": "hcs-primary",
                "copy-clipboard": "hcs-primary",
            },
            messageType: MessageBox.MB_INFO,
            title: "",
            /* text: "", --> We access this property directly via "options" since the "objectAssignDeep" function fails for DOM elements */
            modal: true,
            closeOnEscape: true,
            titleCloseButton: false,
            classNames: "",
            onOpen: () => { },
            onOk: function() { this.close(); },
            onCancel: function() { this.close(); },
            onYes: function() { this.close(); },
            onNo: function() { this.close(); },
            onClose: function() { this.close(); },
            onCopyClipboard: function() { this.close(); }
        }, options);

        this.instance("h1.hcs-title").innerHTML = `
            <svg icon-id="icn-027-1-error" version="2.0">
                <use href="#icn-027-1-error"></use>
            </svg>` + opts.title;

        if(typeof options.text === "object") {
            this.instance(".hcs-message-text").textContent = "";
            this.instance(".hcs-message-text").append(options.text);
        } else {
            this.instance(".hcs-message-text").innerHTML = options.text || "";
        }

        if(opts.titleCloseButton) {
            this.instance("h1.hcs-title").innerHTML += `<a class="hcs-title-close">
                    <svg icon-id="icn-002-2-close" version="2.0">
                        <use href="#icn-002-2-close"></use>
                    </svg>
                </a>`;
            this.instance("h1.hcs-title > a.hcs-title-close").addEventListener("click", () => { opts.onClose.call(self, {}); }, { once: true });
        }

        document.addEventListener("keyup", (e) => {
            if(["Esc", "Escape"].includes(e.key) && opts.closeOnEscape) {
                opts.onClose.call(self, {});
            }
        }, { once: true });

        let classNames = [ this.baseClass ];

        if(opts.classNames.length) {
            if(typeof (opts.classNames) === "string") {
                classNames = classNames.concat(opts.classNames.split(" "));
            } else {
                classNames = classNames.concat(opts.classNames);
            }
        }

        switch(opts.messageType) {
            case MessageBox.MB_INFO:
                classNames.push("hcs-info");
                break;

            case MessageBox.MB_QUESTION:
                classNames.push("hcs-question");
                break;

            case MessageBox.MB_WARN:
                classNames.push("hcs-warn");
                break;

            case MessageBox.MB_ERROR:
                classNames.push("hcs-error");
                break;
        }

        this.instance().className = classNames.join(" ");

        this.instance(".hcs-buttons").innerHTML = "";

        if((opts.buttons & MessageBox.MB_OK) === MessageBox.MB_OK) {
            this.instance(".hcs-buttons").appendChild(this.createButton("ok", opts));
            this.instance(".hcs-buttons BUTTON.ok").addEventListener("click", () => { opts.onOk.call(self, {}); }, { once: true });
        }

        if((opts.buttons & MessageBox.MB_CANCEL) === MessageBox.MB_CANCEL) {
            this.instance(".hcs-buttons").appendChild(this.createButton("cancel", opts));
            this.instance(".hcs-buttons BUTTON.cancel").addEventListener("click", () => { opts.onCancel.call(self, {}); }, { once: true });
        }

        if((opts.buttons & MessageBox.MB_YES) === MessageBox.MB_YES) {
            this.instance(".hcs-buttons").appendChild(this.createButton("yes", opts));
            this.instance(".hcs-buttons BUTTON.yes").addEventListener("click", () => { opts.onYes.call(self, {}); }, { once: true });
        }

        if((opts.buttons & MessageBox.MB_NO) === MessageBox.MB_NO) {
            this.instance(".hcs-buttons").appendChild(this.createButton("no", opts));
            this.instance(".hcs-buttons BUTTON.no").addEventListener("click", () => { opts.onNo.call(self, {}); }, { once: true });
        }

        if((opts.buttons & MessageBox.MB_CLOSE) === MessageBox.MB_CLOSE) {
            this.instance(".hcs-buttons").appendChild(this.createButton("close", opts));
            this.instance(".hcs-buttons BUTTON.close").addEventListener("click", () => { opts.onClose.call(self, {}); }, { once: true });
        }

        if((opts.buttons & MessageBox.MB_CLIPBOARD) === MessageBox.MB_CLIPBOARD) {
            this.instance(".hcs-buttons").appendChild(this.createButton("copy-clipboard", opts));
            this.instance(".hcs-buttons BUTTON.copy-clipboard").addEventListener("click", () => { opts.onCopyClipboard.call(self, {}); }, { once: false });
        }

        if(opts.modal) {
            this.hcs.api.instance().classList.add("hcs-message-box-modal");
        } else {
            this.hcs.api.instance().classList.remove("hcs-message-box-modal");
        }

        this.hcs.api.instance().classList.add("hcs-msg-box-visible");

        opts.onOpen.call(this, {});
    }

    createButton(buttonName, options) {
        let buttonEl = document.createElement("button");

        buttonEl.className = ["hcs-button", "hcs-visible"].concat([buttonName, options.buttonClassCategories[buttonName]]).join(" ");
        buttonEl.innerHTML = options.buttonTexts[buttonName];

        return buttonEl;
    }

    /**
     * Close message dialog.
     */
    close() {
        this.hcs.api.instance().classList.remove("hcs-msg-box-visible");
        this.hcs.api.instance().classList.remove("hcs-message-box-modal");
        this.instance(".hcs-buttons").innerHTML = "";
    }

    /**
     * Select DOM item(s) inside message box.
     * @param {string} [subSelector] - Additional subselector. When not specified this functions returns the main
     * message box element.
     * @param {boolean} [alwaysArray=false] - When query result only contains more than one item an array will be returned,
     * otherwise only the single result item. Set this parameter to true to also return an array when there is only a single item result.
     * @returns {any} Returns DOM element(s) result of selector. When selector covers multiple items an array will be returned, otherwise only the single item.
     * With the alwaysArray=true property you will also receive an array for single items.
     */
    instance(subSelector, alwaysArray = false) {
        let inst = this.selector;

        if(arguments.length > 0) {
            inst = inst.querySelectorAll(subSelector);

            if(inst !== null && inst.length === 1 && !alwaysArray) {
                return inst[0];
            } else {
                return inst;
            }
        }

        return inst;
    }
}

MessageBox.MB_NONE = 0;
MessageBox.MB_OK = 1;
MessageBox.MB_CANCEL = 2;
MessageBox.MB_YES = 4;
MessageBox.MB_NO = 8;
MessageBox.MB_CLOSE = 16;
MessageBox.MB_CLIPBOARD = 32;

MessageBox.MB_INFO = 1;
MessageBox.MB_QUESTION = 2;
MessageBox.MB_WARN = 4;
MessageBox.MB_ERROR = 8;
